import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  useTeamMembers, useTeamInfo, setMemberRole, removeMember, leaveTeam,
} from '../tareas/api';
import type { TeamMember, TeamRole } from '../tareas/api';
import { useDialog } from '../ui/Dialogs';

const ROLE_LABEL: Record<TeamRole, string> = {
  owner: 'Propietario',
  admin: 'Admin',
  member: 'Miembro',
};

const ROLE_OPTS: Array<{ key: 'admin' | 'member'; label: string; desc: string }> = [
  { key: 'admin', label: 'Admin', desc: 'Puede cambiar la configuración del equipo, invitar y gestionar miembros.' },
  { key: 'member', label: 'Miembro', desc: 'Participa en los hilos. No gestiona el equipo ni a los miembros.' },
];

const Check = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export default function TeamMembers() {
  const { profile, reloadProfile } = useAuth();
  const navigate = useNavigate();
  const { team } = useTeamInfo();
  const { members, loading, refetch } = useTeamMembers();

  const me = members.find((m) => m.id === profile?.id);
  const iAmAdmin = me?.role === 'owner' || me?.role === 'admin';

  const afterLeave = async () => {
    await reloadProfile();
    navigate('/bandeja', { replace: true });
  };

  return (
    <section style={styles.card}>
      <h2 style={styles.cardTitle}>Miembros ({members.length})</h2>
      <p style={styles.help}>
        Los <strong>admins</strong> gestionan el equipo, invitan y administran miembros.
        Los <strong>miembros</strong> participan en los hilos. El propietario no se puede degradar.
      </p>

      {loading ? (
        <p style={styles.muted}>Cargando…</p>
      ) : (
        <div style={styles.list}>
          {members.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              teamName={team?.nombre ?? 'el equipo'}
              isOwner={m.id === team?.owner_id}
              isSelf={m.id === profile?.id}
              canManage={iAmAdmin}
              onChanged={refetch}
              onLeft={afterLeave}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface RowProps {
  member: TeamMember;
  teamName: string;
  isOwner: boolean;
  isSelf: boolean;
  canManage: boolean;
  onChanged: () => Promise<void>;
  onLeft: () => Promise<void>;
}

function MemberRow({ member, teamName, isOwner, isSelf, canManage, onChanged, onLeft }: RowProps) {
  const { confirm } = useDialog();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const canEditRole = canManage && !isOwner; // puedo cambiar su rol
  const canLeave = isSelf && !isOwner;        // puedo abandonar
  const canRemove = canManage && !isSelf && !isOwner; // puedo eliminarle
  const hasMenu = canEditRole || canLeave || canRemove;

  const run = async (fn: () => Promise<void>, after: () => Promise<void>) => {
    setBusy(true); setError('');
    try { await fn(); await after(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Error'); }
    finally { setBusy(false); }
  };

  const changeRole = (role: 'admin' | 'member') => {
    if (role === member.role) return;
    run(() => setMemberRole(member.id, role), onChanged);
  };
  const remove = async () => {
    const ok = await confirm({
      title: 'Eliminar del equipo',
      message: `¿Eliminar a ${member.nombre} del equipo? Perderá el acceso a sus hilos.`,
      confirmLabel: 'Eliminar',
      danger: true,
    });
    if (!ok) return;
    run(() => removeMember(member.id), onChanged);
  };
  const leave = async () => {
    const ok = await confirm({
      title: 'Abandonar el equipo',
      message: `¿Seguro que quieres abandonar ${teamName}? Perderás el acceso a sus hilos.`,
      confirmLabel: 'Abandonar',
      danger: true,
    });
    if (!ok) return;
    run(() => leaveTeam(), onLeft);
  };

  const footer = canLeave
    ? { label: 'Abandonar el equipo…', onClick: leave }
    : canRemove
      ? { label: 'Eliminar del equipo…', onClick: remove }
      : null;

  return (
    <div style={styles.row}>
      <span style={{ ...styles.avatar, background: member.color }}>
        {member.nombre.charAt(0).toUpperCase()}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.name}>
          {member.nombre}
          {isSelf && <span style={styles.youTag}>tú</span>}
        </div>
        {error && <div style={styles.error}>{error}</div>}
      </div>

      {hasMenu ? (
        <RoleMenu
          role={member.role}
          editable={canEditRole}
          busy={busy}
          onSelectRole={changeRole}
          footer={footer}
        />
      ) : (
        <span style={styles.roleStatic}>{ROLE_LABEL[member.role]}</span>
      )}
    </div>
  );
}

function RoleMenu({
  role, editable, busy, onSelectRole, footer,
}: {
  role: TeamRole;
  editable: boolean;
  busy: boolean;
  onSelectRole: (r: 'admin' | 'member') => void;
  footer: { label: string; onClick: () => void } | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} disabled={busy} style={styles.trigger}>
        <span>{busy ? '…' : ROLE_LABEL[role]}</span>
        <span style={{ display: 'flex', color: '#94a3b8' }}><Chevron /></span>
      </button>

      {open && (
        <div style={styles.menu}>
          {editable && ROLE_OPTS.map((o) => {
            const active = o.key === role;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => { setOpen(false); onSelectRole(o.key); }}
                style={{ ...styles.menuItem, ...(active ? styles.menuItemActive : null) }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.menuItemTitle}>{o.label}</div>
                  <div style={styles.menuItemDesc}>{o.desc}</div>
                </div>
                {active && <span style={styles.menuCheck}><Check /></span>}
              </button>
            );
          })}
          {footer && (
            <button
              type="button"
              onClick={() => { setOpen(false); footer.onClick(); }}
              style={{ ...styles.menuFooter, ...(editable ? styles.menuFooterDivided : null) }}
            >
              {footer.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 },
  help: { fontSize: 13, color: '#64748b', margin: '8px 0 16px', lineHeight: 1.5 },
  list: { display: 'flex', flexDirection: 'column' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', borderBottom: '1px solid #f1f5f9' },
  avatar: {
    width: 36, height: 36, minWidth: 36, borderRadius: '50%', color: '#fff',
    fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  name: { fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  youTag: { fontSize: 11, color: '#64748b', fontWeight: 600, marginLeft: 6 },
  roleStatic: { fontSize: 13, fontWeight: 600, color: '#64748b', padding: '7px 4px' },
  trigger: {
    display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e2e8f0',
    borderRadius: 8, padding: '7px 10px 7px 12px', fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer',
  },
  menu: {
    position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 280,
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    boxShadow: '0 12px 28px rgba(15,23,42,0.16)', overflow: 'hidden', zIndex: 200, padding: 4,
  },
  menuItem: {
    display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', textAlign: 'left',
    background: 'transparent', border: 'none', cursor: 'pointer', padding: '10px 12px', borderRadius: 8,
  },
  menuItemActive: { background: '#eff5ff' },
  menuItemTitle: { fontSize: 13.5, fontWeight: 700, color: '#0f172a' },
  menuItemDesc: { fontSize: 12, color: '#64748b', lineHeight: 1.4, marginTop: 2 },
  menuCheck: { display: 'flex', marginTop: 2 },
  menuFooter: {
    width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer',
    padding: '10px 12px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, color: '#dc2626',
  },
  menuFooterDivided: { borderTop: '1px solid #f1f5f9', borderRadius: 0, marginTop: 4 },
  muted: { color: '#94a3b8', fontSize: 14, margin: 0 },
  error: { color: '#dc2626', fontSize: 12, marginTop: 2 },
};
